import { Component, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { RouteProvider } from '../../providers/route/route';
import { SendFlowProvider } from '../../providers/send-flow/send-flow';
import { ResourceProvider } from '../../providers/resource/resource';
import { getAllInfoByISO } from 'iso-country-currency';
import { LoggerEvent, LoggerProvider } from '../../providers/logger/logger';
import { getCurrencyDisplayName } from '../../pipes/currency-pipe';

@Component({
  selector: 'rmt-currency',
  templateUrl: 'rmt-currency.html'
})
export class RmtCurrencyComponent {
  @ViewChild('input') input;
  @ViewChild('input', { read: ElementRef }) inputRef;
  @Input() country;
  @Input() currencies;
  @Input() title;
  @Input() placeholder;
  @Input() precision: number = 2;
  @Input() hideInput: boolean = false;
  @Input() inputId: string = "";
  @Input() selectable: boolean= false;
  @Input() canShowDecimalPlaceHolder: boolean= true;
  @Output() ionFocus = new EventEmitter<any>();
  @Output() ionBlur = new EventEmitter<any>();
  @Output() ionChange = new EventEmitter<any>();
  @Output() ionSelect = new EventEmitter<any>();
  @Output() keyPress = new EventEmitter<any>();
  amount;
  selectedCurrency;
  changeTimeout;
  allowUpdate: boolean = true;
  lastValue;
  headline;
  colorOutline;
  countries;
  isoError;
  isUserTyping: boolean = false;
  navInfo = this.routeProvider.pages.ListActionsheetPage;
  inputPlaceHolder = "0.00"
  constructor(private routeProvider: RouteProvider,
              private resourceProvider: ResourceProvider,
              private sendFlowProvider: SendFlowProvider) {
    this.countries = this.resourceProvider.getSendCountries()
  }

  ngAfterViewInit() {
    if(this.canShowDecimalPlaceHolder === false){
      this.inputPlaceHolder = "0";
    }
  }

  setBorderColor(color) {
    this.colorOutline = color;
  }

  /**
   * Sets the outline drawn by the amount-error-border / amount-input-border classes. Both are a
   * box-shadow rather than a border, so this is named for what it actually sets.
   */
  setBoxShadowColor(color: string) {
    this.colorOutline = color;
  }

  setAmount(amount) {
    this.amount = amount;
    if (!this.isUserTyping) {
      this.input.value = amount;
    }
  }

  setCurrency(currency) {
    this.selectedCurrency = currency;
  }

  changeAmount(ev) {
    this.keyPress.emit(ev.value);
    this.amount = ev.value;

    if (this.changeTimeout)
      window.clearTimeout(this.changeTimeout);
    if (!this.allowUpdate)
      return;

    // capture now: blur can reset isUserTyping before the timeout fires; programmatic setAmount() also lands here but never sets it
    const userTyped = this.isUserTyping;
    this.changeTimeout = window.setTimeout(() => {
      this.triggerChange(true, userTyped);
    }, 200);
  }


  changeCurrency(ev) {
    if (Array.isArray(ev) && ev.length === 0) // bug from ionChange: null currency will be sent as Array
      ev = null;
    this.selectedCurrency = ev;

    if (!this.allowUpdate)
      return;
    this.triggerChange(false);
  }

  triggerChange(changedAmount, userTyped = false) {
    if (this.changeTimeout)
      window.clearTimeout(this.changeTimeout);
    this.changeTimeout = null;
    this.ionChange.emit({ amount: this.amount, currency: this.selectedCurrency, changedAmount: changedAmount, userTyped: userTyped });
  }

  focus() {
    if (this.inputRef)
      this.inputRef.nativeElement.getElementsByTagName('input')[0].focus();
  }

  onFocus(ev) {
    this.ionFocus.emit(ev);
    if(this.colorOutline !== 'red')
      this.setBorderColor('orange');
    // Force cursor to the right (end of input)
    const inputElement = this.inputRef.nativeElement.getElementsByTagName('input')[0];
    if (inputElement) {
      // For number inputs, we need to temporarily change type to text to move cursor
      //This workaround is necessary because number inputs don't support cursor positioning natively
      const originalType = inputElement.type;
      inputElement.type = 'text';
      const length = inputElement.value.length;
      inputElement.setSelectionRange(length, length);
      inputElement.type = originalType;
    }
  }

  onBlur(ev) {
    this.ionBlur.emit(ev);
    if(this.colorOutline !== 'red')
      this.setBorderColor('black');
    this.isUserTyping = false;
  }

  checkAmountPreKey() {
    if (this.validateAmount())
      this.lastValue = this.input.value;
    this.setBorderColor('orange');
    this.isUserTyping = true;
  }

  changeAmountPostKey(ev) {
    this.isUserTyping = true; // (input) only fires for real user edits (incl. paste), never for programmatic value sets
    if (!this.validateAmount() || (ev.data && !this.input.value))
      this.input.value = this.lastValue;
  }

  validateAmount() {
    if (this.input.value.length === 0)
      return true;

    const reg = new RegExp('^[0-9]+(\\.[0-9]{0,' + this.precision + '})?$');
    const result = reg.test(this.input.value);
    return result;
  }

  getSelectId() {
    if (this.inputId)
      return this.inputId + 'Select'
    else
      return ""
  }

  getSelectedCurrency() {
    if (this.selectedCurrency)
      return getCurrencyDisplayName(this.selectedCurrency);
    if (this.country) {
      let country = this.country;
      if (this.country === 'UK') country = 'GB'; // UK is not a valid ISO code, use GB instead
      if (this.country === 'KV') country = 'XK'; // Kosovo uses XK as ISO code
      try {
        return getCurrencyDisplayName(getAllInfoByISO(country).currency);
      } catch (ex) {
        this.logCurrencyIsoError(country, ex);
        return 'Error';
      }
    }
    return '';
  }

  getCountryImage() {
    if (this.country)
      return 'assets/countryFlagSvg/' +this.country.toUpperCase() + '.svg';
    return '';
  }

  async selectCurrency() {
    if (!this.selectable)
      return;

    const params: any = await this.sendFlowProvider.getCountryPickerParams((item, viewCtrl) => {
      if (item.country)
        this.country = item.country;
      else
        this.country = item.value;
      this.ionSelect.emit(item);
      viewCtrl.dismiss();
    }, this.country);

    if (this.country && this.currencies && this.currencies.length > 1 && params) {
      if (params.itemSelected)
        params.itemSelected.value += '_' + this.selectedCurrency;
      const promise = params.favoritePromise;

      params.currencyPromise = new Promise(async(resolve) => {
        let items: any = await promise;
        const countries = await this.countries;
        if (items && countries) {
          const country = countries.find((item) => item.value === this.country);
          items = items.filter((item) => item.value !== this.country);
          const currencies = [];
          for (const currency of this.currencies) {
            const currencyDisplay = getCurrencyDisplayName(currency.currencyCode);
            currencies.push({
              text: ((country) ? country.text : this.country) + ' - '+ currencyDisplay,
              value: this.country + '_' + currency.currencyCode,
              country: this.country,
              currency: currency.currencyCode
            })
          }
          items = currencies
        }
        resolve(items);
      });
    }
    this.routeProvider.openModal(this.routeProvider.pages.SelectPickerPage, params);
  }

  private logCurrencyIsoError(country, ex) {
    if (this.isoError !== country) {
      LoggerProvider.error(ex, LoggerEvent.ComponentException, 'Currency ISO code not found for country: ' + this.country);
      this.isoError = country;
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/rmt-currency/rmt-currency.ts